import { useCallback, useEffect, useState } from "react";

import { useGroupsApi } from "./useGroupsHook";

// Module-level cache + in-flight promise so the full groups list is fetched
// once per session and shared across every mount (a page can render several
// Assigned-Team cards). Membership edits are admin-only and rare; a session
// cache is the right tradeoff for a read-only dropdown source.
let _groupsPromise = null;
let _groupsCache = null;

const membersToOptions = (group) =>
  (group?.members || [])
    .map((m) => ({ label: m?.fullName || m?.email, value: m?.email }))
    .filter((o) => o.value)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

/**
 * Resolve org RBAC groups into role-dropdown options.
 *
 * Fetches the groups list once (GET /groups, cached) and maps each group's
 * members to `{ label: fullName, value: email }`. `optionsForGroup(name,
 * fallback)` returns the group's members when the group exists AND has any,
 * else the caller's static `fallback` roster — so a role populates from its
 * live group and gracefully degrades to the hardcoded list when the group is
 * missing or empty. Name match is case-insensitive (the backend stores exact
 * names like "US Underwriters" / "Closers").
 */
export const useOrgGroupOptions = () => {
  const { listGroups } = useGroupsApi();
  const [byName, setByName] = useState(_groupsCache);

  useEffect(() => {
    if (_groupsCache) {
      setByName(_groupsCache);
      return;
    }
    if (!_groupsPromise) {
      _groupsPromise = listGroups()
        .then((groups) => {
          const map = {};
          (groups || []).forEach((g) => {
            if (g?.name) map[String(g.name).toLowerCase()] = membersToOptions(g);
          });
          _groupsCache = map;
          return map;
        })
        .catch(() => {
          // Cache the empty result so callers fall back to their rosters and we
          // don't re-request on every mount after a failure.
          _groupsCache = {};
          return _groupsCache;
        });
    }
    let active = true;
    _groupsPromise.then((map) => {
      if (active) setByName(map);
    });
    return () => {
      active = false;
    };
  }, [listGroups]);

  const optionsForGroup = useCallback(
    (groupName, fallback) => {
      const opts = byName?.[String(groupName).toLowerCase()];
      return opts && opts.length > 0 ? opts : fallback;
    },
    [byName],
  );

  return { optionsForGroup, groupsLoaded: byName != null };
};

export default useOrgGroupOptions;
